(ns ballot-scout-v2-frontend.db
  (:require [clojure.data :as d]
            [clojure.set :as set]
            [com.stuartsierra.mapgraph :as mg]))

(def id-attributes
  "Each key is the primary id of the entity: the attribute MapGraph will index on.
   Each value stores the entity's colloquial name, as well as any secondary unique fields to index on."
  {:partner/id      {:entity-name :partner
                     :secondary-indexes {:slug :partner/slug}}
   :jurisdiction/id {:entity-name :jurisdiction
                     :secondary-indexes {:slug :jurisdiction/slug}}
   :election/id     {:entity-name :election
                     :secondary-indexes {:slug-and-partner-id
                                         (fn [{:election/keys [slug partner-id]}]
                                           [slug partner-id])}}
   :import/id       {:entity-name :import
                     :secondary-indexes {}}
   :disposition/id  {:entity-name :disposition
                     :secondary-indexes {}}})

(def full-import-pattern
  ['*])

(def full-election-pattern
  ['*])

(def full-jurisdiction-pattern
  ['* {:jurisdiction/current-election full-election-pattern}])

(def full-partner-pattern
  ['* {:partner/jurisdictions full-jurisdiction-pattern}])

(def full-disposition-pattern
  ['*])

(defn new-index
  []
  (apply mg/add-id-attr (mg/new-db) (keys id-attributes)))

(def default-db
  "Nest the MapGraph index under ::index in our db."
  {:name "re-frame"
   :show-banner? true
   ::index (new-index)
   ::secondary-index {}
   ;; xref table that maps jurisdiction slug to a set of election mapgraph refs
   ::jurisdiction-elections {}
   ;; xref table that maps partner slug to a set of election mapgraph refs
   ::partner-elections {}})

(defn- add-to-secondary-indexes
  "Index entity in ::secondary-indexes based on its lookup ref."
  [secondary-index [id-attr _ :as lookup-ref] entity]
  (let [{:keys [entity-name secondary-indexes]} (get id-attributes id-attr)]
    (reduce-kv (fn [sec-idx idx-name idx-fn]
                 (-> sec-idx
                     (assoc-in [entity-name idx-name (idx-fn entity)] lookup-ref)
                     (update-in [entity-name ::all] (fnil #(conj % lookup-ref) #{}))))
               secondary-index
               secondary-indexes)))

(defn add-entity
  "Index the entity (and all sub-entities).
   Then, use clojure.data/diff to isolate those entities added, iterate over them,
   and add them to secondary indexes."
  [{::keys [index] :as db} entity]
  (let [updated-index (mg/add index entity)
        added-or-changed-entities (->> index (d/diff updated-index) first keys (select-keys updated-index))]
    (-> db
        (assoc ::index updated-index)
        (update ::secondary-index #(reduce-kv add-to-secondary-indexes % added-or-changed-entities)))))

(defn pull
  [db pattern lookup-ref]
  (when (and pattern lookup-ref)
    (mg/pull (::index db) pattern lookup-ref)))

(defn get-lookup-ref-from-sec-idx
  [db entity-name sec-idx value]
  (get-in db [::secondary-index entity-name sec-idx value]))

(defn all-for-attribute
  [db pattern entity-name]
  (->> [::secondary-index entity-name ::all]
       (get-in db)
       (map #(pull db pattern %))))

;; Partner functions

(defn partner-by-id
  [db partner-id & {:keys [full] :or {full true}}]
  (let [pattern (if full full-partner-pattern '[*])]
    (pull db pattern [:partner/id partner-id])))

(defn partner-by-slug
  [db partner-slug & {:keys [full] :or {full true}}]
  (let [pattern (if full full-partner-pattern '[*])
        lookup-ref (get-lookup-ref-from-sec-idx db :partner :slug partner-slug)]
    (pull db pattern lookup-ref)))

(defn all-partners
  [db & {:keys [full] :or {full true}}]
  (let [pattern (if full full-partner-pattern '[*])]
    (all-for-attribute db pattern :partner)))

;; Jurisdiction functions

(defn jurisdiction-by-id
  [db jurisdiction-id & {:keys [full] :or {full true}}]
  (let [pattern (if full full-jurisdiction-pattern '[*])]
    (pull db pattern [:jurisdiction/id jurisdiction-id])))

(defn jurisdiction-by-slug
  [db jurisdiction-slug & {:keys [full] :or {full true}}]
  (let [pattern (if full full-jurisdiction-pattern '[*])
        lookup-ref (get-lookup-ref-from-sec-idx db :jurisdiction :slug jurisdiction-slug)]
    (pull db pattern lookup-ref)))

;; Election functions

(defn election-by-id
  [db election-id & {:keys [full] :or {full true}}]
  (let [pattern (if full full-election-pattern '[*])]
    (pull db pattern [:election/id election-id])))

(defn election-by-slug-and-partner-id
  [db election-slug partner-id & {:keys [full] :or {full true}}]
  (let [pattern (if full full-election-pattern '[*])
        lookup-ref (get-lookup-ref-from-sec-idx db :election :slug-and-partner-id [election-slug partner-id])]
    (pull db pattern lookup-ref)))

(defn add-elections-to-jurisdiction
  [{::keys [index] :as db} jurisdiction-slug elections]
  (let [election-refs (set (map (partial mg/ref-to index) elections))]
    (as-> db $
      (reduce add-entity $ elections)
      (update-in $ [::jurisdiction-elections jurisdiction-slug] set/union election-refs))))

(defn jurisdiction-elections
  [{::keys [jurisdiction-elections] :as db} jurisdiction-slug & {:keys [full] :or {full true}}]
  (let [pattern (if full full-election-pattern '[*])]
    (some->> (get jurisdiction-elections jurisdiction-slug)
             (map (partial pull db pattern)))))

(defn jurisdiction-has-election?
  [{::keys [index jurisdiction-elections]} jurisdiction-slug election]
  (let [election-ref (mg/ref-to index election)]
    (-> jurisdiction-elections
        (get jurisdiction-slug)
        (contains? election-ref))))

(defn add-elections-to-partner
  [{::keys [index] :as db} partner-slug elections]
  (let [election-refs (set (map (partial mg/ref-to index) elections))]
    (as-> db $
      (reduce add-entity $ elections)
      (update-in $ [::partner-elections partner-slug] set/union election-refs))))

(defn partner-elections
  [{::keys [partner-elections] :as db} partner-slug & {:keys [full] :or {full true}}]
  (let [pattern (if full full-election-pattern '[*])]
    (some->> (get partner-elections partner-slug)
             (map (partial pull db pattern)))))

;; Import functions

(defn import-by-id
  [db import-id & {:keys [full] :or {full true}}]
  (let [pattern (if full full-import-pattern '[*])]
    (pull db pattern [:import/id import-id])))

;; Disposition functions

(defn disposition-by-id
  [db dispo-id & {:keys [full] :or {full true}}]
  (let [pattern (if full full-disposition-pattern '[*])]
    (pull db pattern [:disposition/id dispo-id])))
